import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, GitBranch } from "lucide-react";

const EXAMPLE = `1) Annie Walters (Dell)
   John, Mary, Peter
2) Yorkwin Walters-Nwofor
   Children of Yorkwin - Lisa, Tom`;

export function ImportDialog({ open, onOpenChange, onDone }) {
  const [text, setText] = useState("");
  const [ancestor, setAncestor] = useState("Walters");
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [people, setPeople] = useState(null);

  const reset = () => { setText(""); setAncestor("Walters"); setPeople(null); };

  const parse = async () => {
    if (!text.trim()) { toast.error("Paste your family list first"); return; }
    setParsing(true);
    try {
      const data = await api.importParse(text, ancestor);
      setAncestor(data.ancestor_name || "Walters");
      setPeople(data.people || []);
      if (!data.people?.length) toast.info("No people detected — check the format");
    } catch { toast.error("Could not read the list"); }
    finally { setParsing(false); }
  };

  const updatePerson = (i, patch) => setPeople((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const removePerson = (i) => setPeople((ps) => ps.filter((_, idx) => idx !== i));

  const commit = async () => {
    setCommitting(true);
    try {
      const res = await api.importCommit({ ancestor_name: ancestor, create_ancestor: true, people });
      toast.success(`Imported ${res.created} people`);
      onDone?.();
      reset();
      onOpenChange(false);
    } catch { toast.error("Import failed"); }
    finally { setCommitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-[#082D4A]">
            <Upload size={22} /> Import a whole family
          </DialogTitle>
          <DialogDescription>
            Paste a numbered family list. Everyone becomes one connected tree under a shared ancestor.
            Nothing is saved until you review and confirm.
          </DialogDescription>
        </DialogHeader>

        {!people ? (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Shared ancestor name</Label>
              <Input data-testid="import-ancestor-input" value={ancestor} onChange={(e) => setAncestor(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Family list</Label>
              <Textarea data-testid="import-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={10} placeholder={EXAMPLE} className="font-mono text-sm" />
            </div>
            <Button onClick={parse} disabled={parsing} data-testid="import-parse-button" className="w-full bg-[#D4A017] text-white hover:bg-[#B0850F]">
              {parsing ? "Reading…" : "Read & preview"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3 py-2" data-testid="import-preview">
            <div className="flex items-center gap-2 rounded-xl bg-[#E8EEF3] p-2">
              <GitBranch size={16} className="text-[#082D4A]" />
              <Label className="text-sm">Shared ancestor:</Label>
              <Input data-testid="import-preview-ancestor" value={ancestor} onChange={(e) => setAncestor(e.target.value)} className="h-8 bg-white" />
            </div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">
              {people.length} people detected — edit or remove any, fix links, then import
            </div>
            <div className="space-y-2">
              {people.map((p, i) => (
                <div key={p.tmp_id} data-testid={`import-person-${i}`} className="grid grid-cols-12 items-center gap-2 rounded-xl border border-[#D8E0E8] bg-white p-2">
                  <Input value={p.full_name} onChange={(e) => updatePerson(i, { full_name: e.target.value })} className="col-span-4 h-8" data-testid={`import-person-${i}-name`} />
                  <Input value={p.nickname || ""} placeholder="nickname" onChange={(e) => updatePerson(i, { nickname: e.target.value })} className="col-span-3 h-8" />
                  <div className="col-span-4">
                    <Select value={p.parent || "ancestor"} onValueChange={(v) => updatePerson(i, { parent: v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`import-person-${i}-parent`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ancestor">Child of {ancestor} (ancestor)</SelectItem>
                        {people.filter((o) => o.tmp_id !== p.tmp_id).map((o) => (
                          <SelectItem key={o.tmp_id} value={o.tmp_id}>Child of {o.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <button onClick={() => removePerson(i)} className="col-span-1 flex justify-center text-red-500 hover:text-red-700" data-testid={`import-person-${i}-remove`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {people ? (
            <>
              <Button variant="ghost" onClick={() => setPeople(null)}>Back</Button>
              <Button onClick={commit} disabled={committing || !people.length} data-testid="import-confirm-button" className="bg-[#082D4A] text-white hover:bg-[#08223A]">
                {committing ? "Importing…" : `Import ${people.length} people`}
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
